import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useHypoteseListe } from '../api/useHypoteseBehandling.ts';
import { useFjernGrunnlag, useLenkGrunnlag, type SubmissionEvidenceLink } from '../api/useHypoteseGrunnlag.ts';
import { useHypoteseConfig, useHypoteser, type HypothesisSummary } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import type { SubmissionDetail } from '../api/useSubmissions.ts';
import { nummerTekst } from '../hypoteser/Kvittering.tsx';
import { hvem, navnAv, statusNavn } from '../hypoteser/lesning.ts';
import { kortDatoAv } from '../hypoteser/setning.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Velger } from '../skall/Velger.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel, LESER_GRUNN, LESER_ID, tokenVar } from '../status/status.ts';
import { saksnr } from './saksnummer.ts';
import { ANGRE_SEKUNDER, type ToastInnhold } from './Toast.tsx';

/**
 * H3.1 — saken som grunnlag for en hypotese, fra saken i Innspill (konseptsiden del B, skjerm 5–6).
 *
 * - «Bruk som grunnlag …» står ved siden av «Gjør til kjent feil» — samme type handling: koble
 *   saken til noe som finnes. `h` åpner den.
 * - Velgeren er en meny fra en knapp (`Velger`): søk med fokus, de ÅPNE hypotesene gruppert etter
 *   status (P5: fra mottatt til på pause), ↵ legger til den markerte straks (idempotent).
 * - Lenken er en rad under innsenderlinjen med rommets ikon, «Åpne» og «Fjern». «Fjern» holdes
 *   tilbake i angre-vinduet; det finnes ikke noe angre-endepunkt.
 *
 * Lenking krever behandler i Innspill OG en rolle i Hypoteser. `evidenceFor` er UTELATT når
 * kalleren ikke har en rolle i Hypoteser — da vises saken som før, uten lenke og uten rad.
 */

/** Statusene velgeren viser, i den rekkefølgen gruppene står: det som testes, så det som venter, så det som vurderes. */
const AAPNE = ['testes', 'pa_pause', 'valgt', 'vurderes', 'parkert', 'mottatt'] as const;

const statusIkon = (status: string, outcome?: string) => hypoteseOppforing(hypoteseStatusNoekkel(status, outcome));

export function useSakGrunnlag({ sak, kanSkrive, meld }: { sak: SubmissionDetail | null; kanSkrive: boolean; meld: (t: ToastInnhold | null) => void }) {
  const me = useMe();
  const megId = me.data?.id;
  const lenker = sak?.evidenceFor;
  const finnes = lenker !== undefined;
  const config = useHypoteseConfig(finnes);
  // Tallet i grunnlaget og utfallet står i hypotesens detalj, ikke i lenken: hent de få saken er lenket til.
  // Detaljen skriver ingen logg-rad (Kh9).
  const lenkeIder = useMemo(() => (lenker ?? []).map((l) => l.hypothesisId), [lenker]);
  const detaljer = useHypoteser(lenkeIder);
  const lenk = useLenkGrunnlag();
  const fjern = useFjernGrunnlag();
  const [aapen, setAapen] = useState(false);
  const knapp = useRef<HTMLButtonElement>(null);
  const venter = useRef<{ sakId: string; hypoteseId: string; timer: ReturnType<typeof setTimeout>; teller: ReturnType<typeof setInterval>; send: () => void } | null>(null);
  const [skjult, setSkjult] = useState<{ sakId: string; hypoteseId: string } | null>(null);

  // Ny sak: velgeren hørte til den forrige.
  const sakId = sak?.id;
  useEffect(() => setAapen(false), [sakId]);

  const aapne = useCallback(() => {
    if (finnes && kanSkrive) setAapen(true);
  }, [finnes, kanSkrive]);

  const lukk = useCallback(() => setAapen(false), []);

  const feil = useCallback((e: unknown) => meld({ tekst: erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.', feil: true }), [meld]);

  async function leggTil(h: HypothesisSummary) {
    if (!sak) return;
    setAapen(false);
    knapp.current?.focus();
    try {
      await lenk.mutateAsync({ hypoteseId: h.id, sakId: sak.id });
      meld({ tekst: `${saksnr(sak.number)} er lagt til som grunnlag for ${nummerTekst(h.number)}.` });
    } catch (e) {
      feil(e);
    }
  }

  const avbryt = useCallback(() => {
    const v = venter.current;
    if (!v) return;
    clearTimeout(v.timer);
    clearInterval(v.teller);
    venter.current = null;
    setSkjult(null);
    meld(null);
  }, [meld]);

  /** Sender det som venter, nå — når siden forlates, eller en ny fjerning startes. */
  const sendNaa = useCallback(() => {
    const v = venter.current;
    if (!v) return;
    clearTimeout(v.timer);
    clearInterval(v.teller);
    venter.current = null;
    v.send();
  }, []);

  useEffect(() => () => sendNaa(), [sendNaa]);

  function fjernMedAngre(l: SubmissionEvidenceLink) {
    if (!sak || !kanSkrive) return;
    sendNaa();
    const nr = nummerTekst(l.number);
    const s = { sakId: sak.id, hypoteseId: l.hypothesisId };
    let sekunder = ANGRE_SEKUNDER;
    const vis = () => meld({ tekst: `Fjernet fra ${nr} «${l.title}».`, sekunder, angre: avbryt, ikon: 'remove-circle-outline' });
    const send = () => {
      fjern
        .mutateAsync({ hypoteseId: s.hypoteseId, sakId: s.sakId })
        .then(() => meld({ tekst: `${saksnr(sak.number)} er tatt ut av grunnlaget for ${nr}.` }))
        .catch(feil)
        .finally(() => setSkjult((x) => (x?.hypoteseId === s.hypoteseId && x.sakId === s.sakId ? null : x)));
    };
    setSkjult(s);
    vis();
    const teller = setInterval(() => {
      sekunder -= 1;
      if (sekunder > 0) vis();
    }, 1000);
    const timer = setTimeout(() => {
      clearInterval(teller);
      venter.current = null;
      send();
    }, ANGRE_SEKUNDER * 1000);
    venter.current = { ...s, timer, teller, send };
  }

  const synlige = (lenker ?? []).filter((l) => !(skjult && skjult.sakId === sakId && skjult.hypoteseId === l.hypothesisId));

  const rader: ReactNode =
    finnes && synlige.length > 0 ? (
      <div className="panel-grl-rader" data-testid="sak-grunnlag">
        {synlige.map((l) => {
          const nr = nummerTekst(l.number);
          const d = detaljer[lenkeIder.indexOf(l.hypothesisId)]?.data;
          const status = statusNavn(config.data, { status: l.status, outcome: d?.outcome }) || l.status;
          const tall = d ? ` · ${d.evidenceReach} innspill i grunnlaget` : '';
          return (
            <div key={l.hypothesisId} className="panel-grl-rad" data-testid={`sak-grunnlag-${l.number}`}>
              <Ikon navn="telescope-outline" className="panel-grl-ikon" />
              <span className="panel-grl-tekst">
                <b>
                  Grunnlag for {nr} «{l.title}»
                </b>
                <span className="panel-grl-meta">{`${status} · ${hvem(l.linkedBy, megId, 'du')} la til ${kortDatoAv(l.linkedAt)}${tall}`}</span>
              </span>
              <span className="panel-grl-k">
                <Link to={`/h/registeret/${l.number}`} className="panel-knapp-lenke" aria-label={`Åpne ${nr}`}>
                  Åpne
                </Link>
                {kanSkrive ? (
                  <button type="button" className="panel-knapp-lenke panel-grl-fjern" aria-label={`Fjern fra ${nr}`} onClick={() => fjernMedAngre(l)}>
                    Fjern
                  </button>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    ) : null;

  const lenke: ReactNode = finnes ? (
    <span className="panel-knapp-med-grunn">
      <button
        ref={knapp}
        type="button"
        className="panel-knapp-lenke"
        title={kanSkrive ? 'Bruk som grunnlag … (h)' : LESER_GRUNN}
        aria-keyshortcuts="h"
        aria-haspopup="dialog"
        aria-expanded={aapen}
        aria-disabled={kanSkrive ? undefined : true}
        aria-describedby={kanSkrive ? undefined : LESER_ID}
        onClick={kanSkrive ? () => setAapen((a) => !a) : undefined}
        data-testid="bruk-som-grunnlag"
      >
        Bruk som grunnlag …<kbd>h</kbd>
      </button>
      {aapen && sak ? <HypoteseVelger lenket={new Set((lenker ?? []).map((l) => l.hypothesisId))} utloeser={knapp} megId={megId} onVelg={(h) => void leggTil(h)} onLukk={lukk} /> : null}
    </span>
  ) : null;

  return { rader, lenke, aapne };
}

/** Hvem som holder hypotesen, i velgerens meta-linje: eieren når den er valgt, ellers vurdereren. */
function holder(h: HypothesisSummary, megId: string | undefined): string {
  if ((h.status === 'testes' || h.status === 'valgt' || h.status === 'pa_pause') && h.owner) return `eier ${hvem(h.owner, megId, 'deg')}`;
  if ((h.status === 'vurderes' || h.status === 'parkert') && h.reviewer) return `vurderer ${hvem(h.reviewer, megId, 'deg')}`;
  return `fra ${navnAv(h.submittedBy)}`;
}

/**
 * «Bruk som grunnlag for» (skjerm 5): søket er en combobox over lista, ↑/↓ flytter markeringen og
 * ↵ legger til den markerte. Gruppene har statusens ikon og navn (aldri farge alene). En hypotese
 * saken alt er i, står med «alt i grunnlaget» og kan ikke velges.
 */
function HypoteseVelger({ lenket, utloeser, megId, onVelg, onLukk }: { lenket: ReadonlySet<string>; utloeser: RefObject<HTMLElement | null>; megId: string | undefined; onVelg: (h: HypothesisSummary) => void; onLukk: () => void }) {
  const liste = useHypoteseListe({});
  const config = useHypoteseConfig();
  // Ingen tastaturhint på telefonen (skjerm 14): raden trykkes.
  const smal = useSmal();
  const [sok, setSok] = useState('');
  const [aktiv, setAktiv] = useState(0);
  const id = useId();
  const listeId = `${id}-liste`;

  // Hele registeret trengs for å finne de åpne — hent resten av sidene mens velgeren står.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = liste;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const q = sok.trim().toLowerCase();
  const nummer = /^(h-?)?\d+$/.test(q) ? q.replace(/^h-?/, '') : null;
  const aapne = useMemo(() => (liste.data?.pages.flatMap((p) => p.items) ?? []).filter((h) => (AAPNE as readonly string[]).includes(h.status)), [liste.data]);
  const treff = aapne.filter((h) => !q || (nummer !== null ? String(h.number ?? '').includes(nummer) : (h.title ?? '').toLowerCase().includes(q)));
  const grupper = AAPNE.map((status) => ({ status, rader: treff.filter((h) => h.status === status) })).filter((g) => g.rader.length > 0);
  const flat = grupper.flatMap((g) => g.rader);
  // «36 innspill» per hypotese står bare i detaljen (`evidenceReach`) — hentet for de åpne mens velgeren står.
  const aapneIder = useMemo(() => aapne.map((h) => h.id), [aapne]);
  const detaljer = useHypoteser(aapneIder);
  const tallFor = (h: HypothesisSummary): string | null => {
    const d = detaljer[aapneIder.indexOf(h.id)]?.data;
    if (!d) return null;
    return d.evidenceReach === 0 ? 'ingen innspill ennå' : `${d.evidenceReach} innspill`;
  };
  const markert = Math.min(aktiv, Math.max(0, flat.length - 1));

  useEffect(() => setAktiv(0), [q]);

  const tast = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setAktiv((i) => (e.key === 'ArrowDown' ? Math.min(flat.length - 1, i + 1) : Math.max(0, i - 1)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const h = flat[markert];
      if (h && !lenket.has(h.id)) onVelg(h);
    }
  };

  const valgId = (h: HypothesisSummary) => `${id}-${h.id}`;

  return (
    <Velger
      tittel="Bruk som grunnlag for"
      tittelId={`${id}-h`}
      utloeser={utloeser}
      onLukk={onLukk}
      testid="grunnlag-velger"
      fot={
        <>
          <span className="panel-velger-las">Konkluderte og avviste hypoteser står ikke her.</span>
          <Link to="/h/registeret" className="panel-knapp-lenke">
            Åpne Hypoteser
          </Link>
        </>
      }
    >
      <label className="panel-velger-sok">
        <Ikon navn="search-outline" />
        <input
          type="text"
          role="combobox"
          aria-label="Søk i hypotesene"
          aria-expanded="true"
          aria-controls={listeId}
          aria-autocomplete="list"
          aria-activedescendant={flat[markert] ? valgId(flat[markert]) : undefined}
          value={sok}
          onChange={(e) => setSok(e.target.value)}
          onKeyDown={tast}
          placeholder="Tittel eller nummer"
          autoComplete="off"
        />
        {smal ? null : <kbd aria-hidden="true">Esc</kbd>}
      </label>
      {liste.isPending ? (
        <p className="panel-tekst-liten" role="status">
          Henter hypotesene …
        </p>
      ) : liste.isError ? (
        <p className="panel-tekst-liten" role="status">
          <Ikon navn="information-circle-outline" /> Hypotesene kunne ikke hentes.{' '}
          <button type="button" className="panel-knapp-lenke" onClick={() => void liste.refetch()}>
            Prøv igjen
          </button>
        </p>
      ) : flat.length === 0 ? (
        <p className="panel-tekst-liten">{q ? `Ingen hypoteser matcher «${sok.trim()}».` : 'Ingen åpne hypoteser ennå — den første kommer fra Ny hypotese.'}</p>
      ) : (
        <div role="listbox" id={listeId} aria-label="Åpne hypoteser" className="panel-velger-liste">
          {grupper.map((g) => {
            const navn = statusNavn(config.data, { status: g.status }) || g.status;
            return (
              <div key={g.status} role="group" aria-label={navn}>
                <div className="panel-velger-gruppe" aria-hidden="true">
                  {navn}
                </div>
                {g.rader.map((h) => {
                  const i = flat.indexOf(h);
                  const paa = i === markert;
                  const alt = lenket.has(h.id);
                  const o = statusIkon(h.status, h.outcome);
                  return (
                    <div
                      key={h.id}
                      id={valgId(h)}
                      role="option"
                      aria-selected={paa}
                      aria-disabled={alt || undefined}
                      className={`panel-velger-rad${paa ? ' panel-velger-rad-markert' : ''}${alt ? ' panel-velger-rad-dempet' : ''}`}
                      style={{ '--velger-status': tokenVar(o.fg) } as CSSProperties}
                      onPointerEnter={() => setAktiv(i)}
                      onClick={alt ? undefined : () => onVelg(h)}
                    >
                      <Ikon navn={o.ikon} className="panel-velger-statusikon" />
                      <span className="panel-velger-tittel">{h.title}</span>
                      <span className="panel-velger-hoyre">{alt ? 'alt i grunnlaget' : paa && !smal ? 'Legg til ↵' : ''}</span>
                      <span className="panel-velger-meta">
                        {[nummerTekst(h.number), tallFor(h), holder(h, megId)].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </Velger>
  );
}
